<script setup lang="ts">
import { ref, inject } from 'vue';
import { useTrip } from '@/stores/trip';
import { useToast } from '@/composables/useToast';
import { exportRouteToText, downloadAsTextFile } from '@/core/export';

import TripControls from './TripControls.vue';
import TripLegend from './TripLegend.vue';
import StationAutocomplete from './StationAutocomplete.vue';
import TripStopsList from './TripStopsList.vue';
import TripTotals from './TripTotals.vue';
import TripSummary from './TripSummary.vue';
import OptimizeModal from './OptimizeModal.vue';
import MiniToast from './MiniToast.vue';

/**
 * Sidebar (desktop) / bottom-drawer (mobile) regroupant tous les contrôles
 * de l'itinéraire. Sur mobile, c'est un `position: absolute` dans `.app-embed-root`,
 * piloté par l'attribut `data-drawer-state` géré par le composable `useDrawer`.
 *
 * On exporte `sidebarRef` et `handleRef` pour que le parent les passe à `useDrawer`.
 *
 * `openHelp` est fourni par `EuroRailApp` via provide() pour rouvrir l'onboarding
 * depuis le bouton « Aide » du header de cette sidebar.
 */
const trip = useTrip();
const { show: showToast } = useToast();
const openHelp = inject<() => void>('openHelp', () => {});

const sidebarRef = ref<HTMLElement | null>(null);
const handleRef = ref<HTMLElement | null>(null);

defineExpose({ sidebarRef, handleRef });

const optimizeOpen = ref(false);

// ---------- Actions globales ----------

function onAddStopFromAutocomplete(id: string) {
  const ok = trip.addStop(id);
  if (!ok) {
    showToast('Cette ville est déjà la dernière étape', 'error');
  }
}

function onClear() {
  if (trip.state.routeStops.length === 0) return;
  // confirm() reste acceptable : la confirmation utilisateur est en place
  if (!confirm('Effacer toutes les étapes ?')) return;
  trip.clearRoute();
}

function onExport() {
  if (trip.state.routeStops.length === 0) {
    showToast('Aucun itinéraire à exporter', 'error');
    return;
  }
  const content = exportRouteToText(trip.state);
  downloadAsTextFile(content, 'itineraire-eurorail.txt');
  showToast('Itinéraire exporté');
}

async function onShare() {
  if (trip.state.routeStops.length === 0) {
    showToast("Construis d'abord un itinéraire", 'error');
    return;
  }
  const url = trip.getShareURL();

  // 1. Web Share API si dispo (mobile)
  if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
    try {
      await navigator.share({
        title: 'Mon itinéraire EuroRail',
        text: 'Voici mon itinéraire en train et bus :',
        url,
      });
    } catch {
      // utilisateur a annulé, silencieux
    }
    return;
  }

  // 2. Clipboard
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      showToast('Lien copié dans le presse-papiers');
      return;
    } catch {
      /* fallthrough */
    }
  }

  // 3. Fallback ultime : prompt
  window.prompt('Copiez ce lien pour partager votre itinéraire :', url);
}
</script>

<template>
  <aside
    ref="sidebarRef"
    class="app-sidebar bg-surface flex flex-col"
  >
    <!-- Poignée mobile (uniquement visible en <700px via CSS) -->
    <button
      ref="handleRef"
      class="app-sidebar-handle md:hidden flex-shrink-0 w-full py-3 flex items-center justify-center cursor-grab active:cursor-grabbing"
      aria-label="Ouvrir/fermer le panneau"
    >
      <span class="block w-10 h-1 rounded-full bg-text-faint/40"></span>
    </button>

    <!-- Contenu scrollable -->
    <div class="flex-1 overflow-y-auto px-5 py-5 space-y-5">
      <header class="flex items-start justify-between gap-3">
        <div class="flex-1 min-w-0">
          <h2 class="font-display text-2xl leading-tight">Construire votre voyage</h2>
          <p class="text-sm text-text-muted mt-1">
            Ajoutez vos étapes une par une. La carte affiche les villes accessibles selon vos critères.
          </p>
        </div>
        <button
          type="button"
          class="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-surface-muted hover:bg-surface-muted/70 rounded-full text-xs text-text-muted transition-colors duration-160"
          title="Comment ça marche ?"
          @click="openHelp"
        >
          <i class="ti ti-help" aria-hidden="true"></i>
          <span>Aide</span>
        </button>
      </header>

      <TripControls />
      <TripLegend />

      <StationAutocomplete @pick="onAddStopFromAutocomplete" />

      <TripStopsList />

      <TripTotals v-if="trip.state.routeStops.length > 0" />

      <TripSummary />

      <!-- Actions -->
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-surface border border-DEFAULT text-sm font-medium rounded-md hover:bg-surface-muted transition-colors duration-160"
          :disabled="trip.state.routeStops.length < 3"
          title="Réorganiser l'ordre des étapes (TSP)"
          @click="optimizeOpen = true"
        >
          <i class="ti ti-arrows-shuffle" aria-hidden="true"></i>
          Réorganiser
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-surface border border-DEFAULT text-sm font-medium rounded-md hover:bg-surface-muted transition-colors duration-160"
          title="Copier un lien partageable"
          @click="onShare"
        >
          <i class="ti ti-share" aria-hidden="true"></i>
          Partager
        </button>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-surface border border-DEFAULT text-sm font-medium rounded-md hover:bg-surface-muted transition-colors duration-160"
          @click="onExport"
        >
          <i class="ti ti-download" aria-hidden="true"></i>
          Exporter
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-red-700 hover:bg-red-50 border border-red-200 transition-colors duration-160"
          :disabled="trip.state.routeStops.length === 0"
          @click="onClear"
        >
          <i class="ti ti-trash" aria-hidden="true"></i>
          Tout effacer
        </button>
      </div>

      <MiniToast />
    </div>

    <!-- Modale TSP -->
    <OptimizeModal :open="optimizeOpen" @close="optimizeOpen = false" />
  </aside>
</template>

<style scoped>
/* ============================================
   Styles drawer mobile — CSS pur car ils dépendent
   de data-drawer-state sur le PARENT (.app-embed-root).
   Tailwind ne peut pas exprimer ces sélecteurs.
   ============================================ */
.app-sidebar {
  width: var(--sidebar-width);
  height: 100%;
  border-right: 1px solid var(--border);
  overflow: hidden;
}

@media (max-width: 699px) {
  .app-sidebar {
    position: absolute;
    inset: 0;
    width: 100%;
    border-right: 0;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
    transform: translateY(var(--drawer-translate, calc(100% - 124px)));
    transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1500;
  }
  .app-sidebar.dragging {
    transition: none;
    transform: translateY(var(--drag-translate, 0px));
  }
}

.app-sidebar-handle {
  -webkit-tap-highlight-color: transparent;
  touch-action: none;
}
</style>
